import { useEffect, useRef, useState, type RefObject } from "react";
import { Loader2, Plus } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { ApiError } from "@/lib/api";
import { createHermesApi, type HermesChat } from "@/lib/hermes-api";
import { cn } from "@/lib/utils";
import { useHermes } from "./HermesProvider";

function formatChatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function chatLabel(chat: HermesChat, liveTitle?: string): string {
  const title = (liveTitle ?? chat.title)?.trim();
  if (title) return title;
  const preview = chat.preview?.trim();
  if (preview) return preview;
  return `会话 #${chat.id}`;
}

type BrandSiteStudioSessionListProps = {
  enabled?: boolean;
  onSessionChosen?: () => void;
  searchInputRef?: RefObject<HTMLInputElement | null>;
  className?: string;
};

/** 设计工作室场景会话：过滤 brand_site_authoring + 当前站。 */
export function BrandSiteStudioSessionList({
  enabled = true,
  onSessionChosen,
  searchInputRef,
  className,
}: BrandSiteStudioSessionListProps) {
  const {
    accountId,
    chatId,
    brandSiteAuthoring,
    sessionTitles,
    switchBrandSiteStudioSession,
    restartBrandSiteStudioSession,
    isSwitchingSession,
  } = useHermes();
  const [chats, setChats] = useState<HermesChat[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const localSearchRef = useRef<HTMLInputElement>(null);
  const inputRef = searchInputRef ?? localSearchRef;
  const brandSiteId = brandSiteAuthoring?.brandSiteId;

  useEffect(() => {
    if (!enabled || !accountId || !brandSiteId) return;

    let cancelled = false;
    setLoadError(null);
    setLoading(true);

    void createHermesApi(accountId)
      .listChats({ mode: "brand_site_authoring", brand_site_id: brandSiteId })
      .then(({ chats: recent }) => {
        if (!cancelled) setChats(recent);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const message = error instanceof ApiError ? error.message : "加载会话失败";
        setLoadError(message);
        setChats([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, accountId, brandSiteId]);

  async function handleSelect(targetId: number) {
    if (targetId === (pendingId ?? chatId) || pendingId !== null) return;
    setPendingId(targetId);
    try {
      await switchBrandSiteStudioSession(targetId);
      onSessionChosen?.();
    } finally {
      setPendingId(null);
    }
  }

  async function handleNew() {
    if (pendingId !== null || isSwitchingSession) return;
    setPendingId(-1);
    try {
      const result = await restartBrandSiteStudioSession();
      if (result.ok) onSessionChosen?.();
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <div className="flex items-center justify-between border-b border-border px-2 py-1.5">
        <p className="text-xs font-medium text-muted-foreground">官网设计会话</p>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-primary-readable hover:bg-muted/60"
          disabled={pendingId !== null || isSwitchingSession}
          onClick={() => void handleNew()}
        >
          <Plus className="size-3.5" />
          新建
        </button>
      </div>
      <Command className="rounded-none border-0 shadow-none">
        <CommandInput ref={inputRef} placeholder="搜索会话…" />
        <CommandList className="max-h-64">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              加载中…
            </div>
          ) : loadError ? (
            <p className="px-3 py-4 text-xs text-destructive">{loadError}</p>
          ) : (
            <>
              <CommandEmpty>没有匹配的会话</CommandEmpty>
              <CommandGroup>
                {chats.map((chat) => {
                  const active = chat.id === (pendingId && pendingId > 0 ? pendingId : chatId);
                  return (
                    <CommandItem
                      key={chat.id}
                      value={`${chatLabel(chat, sessionTitles[chat.id])} ${chat.id}`}
                      className={cn(active && "bg-primary/10")}
                      onSelect={() => void handleSelect(chat.id)}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm">{chatLabel(chat, sessionTitles[chat.id])}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {formatChatTime(chat.updated_at)}
                        </p>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </Command>
    </div>
  );
}
